import { useCurrentOrg, useSessionTokenGetter } from '@develio/auth/client';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { AnalysisComments } from '../components/AnalysisComments';
import { BEAAttribution } from '../components/BEAAttribution';
import { IncentivesSummaryBox } from '../components/IncentivesSummaryBox';
import { ScenariosPreviewPanel } from '../components/ScenariosPreviewPanel';
import { VersionHistoryPanel } from '../components/VersionHistoryPanel';
import { analytics } from '../lib/posthog';

const API = import.meta.env.VITE_API_URL ?? 'http://localhost:4000';

/** Shared district impact shape (county, ISD, or special district) */
type DistrictImpact = {
  propertyTax: number[];
  serviceCosts: number[];
  annualNetBenefits: number[];
  cumulativeNetBenefits: number[];
  presentValueContribution: number[];
  tenYearNetBenefits: number;
  tenYearPresentValue: number;
};

/** Full RunAnalysisResult shape from the calc engine, as stored in analyses.results */
type AnalysisResults = {
  summary: {
    tenYearNetBenefits: number;
    tenYearPresentValue: number;
    totalIncentive: number;
    tenYearGrossNetBenefits: number;
    perJobIncentiveCost: number;
    rateOfReturn: number | null;
    paybackPeriodYears: number | null;
    grandTotalCityBenefit: number;
    /** Per-entity payback/ROI extension — present only for entities that were computed. */
    perEntity?: {
      county?: { paybackPeriodYears: number | null; rateOfReturn: number | null };
      isd?: { paybackPeriodYears: number | null; rateOfReturn: number | null };
      special?: { paybackPeriodYears: number | null; rateOfReturn: number | null };
    };
  };
  construction: {
    constructionJobs: number;
    totalConstructionCityRevenue: number;
    constructionOutputImpact: number;
    constructionEarningsImpact: number;
  };
  projections: {
    stabilizedTotalJobs: number;
    stabilizedSpinOffJobs: number;
    effectiveTotalJobs: number[];
    totalPayroll: number[];
    netNewSales: number[];
    totalTaxableProperty: number[];
  };
  cityImpact: {
    annualNetBenefits: number[];
    cumulativeNetBenefits: number[];
    presentValueContribution: number[];
    benefitsSubtotal: number[];
    costsSubtotal: number[];
    salesTaxProjectNetNew: number[];
    realPropertyTax: number[];
    bppPropertyTax: number[];
    hotelOccupancyTax: number[];
    utilityFranchiseFees: number[];
    miscTaxesAndFees: number[];
  };
  countyImpact?: DistrictImpact;
  isdImpact?: DistrictImpact;
  specialDistrictImpact?: DistrictImpact;
};

type MonteCarloStats = {
  iterations: number;
  tenYearNetBenefits: { p10: number; p50: number; p90: number; mean: number; stdDev: number };
  netPresentValue: { p10: number; p50: number; p90: number; mean: number; stdDev: number };
  benefitCostRatio: { p10: number; p50: number; p90: number };
};

/** ADR-028: deterministic one-at-a-time sensitivity sweep result. */
type SensitivityStats = {
  metric: 'tenYearNetBenefits' | 'tenYearPresentValue' | 'benefitCostRatio';
  baseMetricValue: number;
  parameters: Array<{
    parameter: string;
    baseValue: number;
    lowValue: number;
    highValue: number;
    perturbationPct: number;
    baseOutput: number;
    lowOutput: number;
    highOutput: number;
    lowDelta: number;
    highDelta: number;
    absMaxDelta: number;
  }>;
};

type WageAnalysisResult = {
  wageAnalysis: {
    impliedAnnualWage: number;
    medianWage: number;
    ratio: number;
    badge: string;
    badgeLabel: string;
    badgeColor: string;
    local: { occupationTitle: string; areaName: string; releaseYear: number; socCode: string };
    citation: string;
  } | null;
  reason: string;
};

const WAGE_BADGE_CLASSES: Record<string, string> = {
  below_market: 'bg-amber-100 text-amber-800 border-amber-300',
  at_market: 'bg-green-100 text-green-800 border-green-300',
  above_market: 'bg-blue-100 text-blue-800 border-blue-300',
  premium: 'bg-purple-100 text-purple-800 border-purple-300',
};

/** Human-readable labels for the sensitivity parameters. */
const SENSITIVITY_PARAM_LABELS: Record<string, string> = {
  totalConstructionSpending: 'Construction spending',
  directJobs: 'Direct jobs',
  directAnnualPayroll: 'Direct annual payroll',
  annualTaxableSales: 'Annual taxable sales',
  operationsEmploymentMultiplier: 'Employment multiplier',
  operationsEarningsMultiplier: 'Earnings multiplier',
  localConstructionCaptureRate: 'Local construction capture',
  displacementRate: 'Displacement rate',
  workerRelocationPct: 'Worker relocation %',
  cityPropertyTaxRate: 'City property tax rate',
  citySalesTaxRate: 'City sales tax rate',
  baseCityCostPerWorker: 'City cost per worker',
  propertyTaxAbatementPct: 'Property tax abatement %',
};

type ScoreData = {
  grade: string;
  score: number;
  gradeColor: string;
  paybackFlag: boolean;
  /** ADR-032: project type this score was computed against. */
  projectType?: 'new_startup' | 'expansion' | 'retention';
  /** ADR-032: true when the grade is directional pending future methodology work. */
  advisory?: boolean;
  advisoryReason?: string | null;
  breakdown: Record<
    string,
    {
      score: number;
      weight: number;
      contribution: number;
      notes: string[];
      /** ADR-032: true when this category is N/A for the project type. */
      notApplicable?: boolean;
    }
  >;
};

const PROJECT_TYPE_LABELS: Record<NonNullable<ScoreData['projectType']>, string> = {
  new_startup: 'New',
  expansion: 'Expansion',
  retention: 'Retention',
};

/** Lifecycle state of the two auto-run jobs. See analysis_job_status pg enum. */
type JobStatus = 'idle' | 'queued' | 'running' | 'ready' | 'failed';

type Analysis = {
  id: string;
  projectName: string;
  status: string;
  createdAt: string;
  inputs: unknown;
  results: AnalysisResults | null;
  monteCarloResults: MonteCarloStats | null;
  sensitivityResults: SensitivityStats | null;
  monteCarloStatus?: JobStatus;
  sensitivityStatus?: JobStatus;
  narrativeGeneratedAt: string | null;
  narrativeExpiresAt: string | null;
  scoreData?: ScoreData | null;
  overridesJson?: {
    fields?: Array<{ path: string; profileValue: number; submittedValue: number }>;
    role?: 'owner' | 'consultant' | null;
    reason?: string | null;
    overriddenAt?: string | null;
    consultantWaiver?: {
      authorizationRecordId: string;
      signerName: string | null;
      signedAt: string | null;
    } | null;
  } | null;
  /** Whether locked-methodology governance is on for this org. The override
   * banner only makes sense when there is a locked profile to diverge from. */
  governanceEnabled?: boolean;
};

/**
 * Formatters are null-safe by design: the calc engine occasionally produces
 * null for fields an analysis didn't compute (e.g. perJobIncentiveCost when
 * jobs = 0). Returning an em-dash beats crashing the whole page via an
 * unguarded toLocaleString call.
 */
function fmtCurrency(n: number | null | undefined) {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
}

function fmtK(n: number | null | undefined) {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return fmtCurrency(n);
}

function fmtNum(n: number | null | undefined, decimals = 1) {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  return n.toLocaleString('en-US', { maximumFractionDigits: decimals });
}

/**
 * Formats a payback period for display. When an entity has zero abatement
 * cost, the engine reports paybackPeriodYears = 0 with rateOfReturn = null,
 * which is not a real "instant payback" and must not render as "0 yrs".
 */
function fmtPaybackYears(years: number | null, rateOfReturn: number | null) {
  if (rateOfReturn === null && years === 0) return 'N/A';
  return years !== null ? `${fmtNum(years)} yrs` : 'N/A';
}

/**
 * Plain-language verdict for the fiscal headline. Turns the signed dollar
 * figures into a one-line, board-ready read of "is this a good deal for the
 * city?" — the same interpretive voice used by Wage Quality and Monte Carlo.
 * A negative result is a legitimate finding (the incentive is a net cost), so
 * the tone is "caution" (amber), never an error.
 */
function fiscalVerdict(
  summary: AnalysisResults['summary'],
  totalJobs: number,
  isRetention: boolean,
): { tone: 'positive' | 'caution'; title: string; detail: string } {
  const total = summary.grandTotalCityBenefit;
  const payback = summary.paybackPeriodYears;
  const positive = total >= 0;
  const jobs = fmtNum(totalJobs, 0);
  const amount = fmtCurrency(Math.abs(total));

  const paybackClause =
    payback !== null
      ? ` The incentive pays for itself in about ${fmtNum(payback, 1)} years.`
      : ' The incentive does not pay back within the 10-year window.';

  if (positive) {
    return {
      tone: 'positive',
      title: isRetention
        ? 'Retaining this employer protects more city revenue than it costs.'
        : 'Net positive for the city over 10 years.',
      detail: `Including construction, this project is projected to return ${amount} more to the city than it costs to support, while supporting about ${jobs} total jobs.${paybackClause}`,
    };
  }

  return {
    tone: 'caution',
    title: isRetention
      ? 'Over 10 years, the cost to retain exceeds the revenue protected.'
      : 'Over 10 years, this project costs the city more than it returns.',
    detail: `Including construction, the city's cost to support this project is projected to exceed the new revenue it generates by ${amount}, even though it supports about ${jobs} total jobs.${paybackClause}`,
  };
}

interface KpiCardProps {
  label: string;
  value: string;
  sub?: string;
}

/**
 * KPI card — glass/depth style. Frosted white tile on a tinted page,
 * soft shadow for floating effect, serif numeral with a subtle gradient.
 */
/**
 * Compact status chip for a background job (Monte Carlo, sensitivity).
 * Renders nothing for 'idle' (pre-auto-run analyses) or when results have
 * already landed — the page sections themselves surface the final artifact.
 */
function JobStatusChip({
  label,
  status,
  hasResults,
}: {
  label: string;
  status: JobStatus | undefined;
  hasResults: boolean;
}) {
  if (hasResults) return null;
  if (!status || status === 'idle' || status === 'ready') return null;
  const styles: Record<'queued' | 'running' | 'failed', string> = {
    queued: 'border-indigo-200 bg-indigo-50 text-indigo-700',
    running: 'border-indigo-300 bg-indigo-100 text-indigo-800',
    failed: 'border-red-200 bg-red-50 text-red-700',
  };
  const verb = status === 'failed' ? 'failed' : status === 'running' ? 'running…' : 'queued';
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium ${
        styles[status as 'queued' | 'running' | 'failed']
      }`}
    >
      <span className="inline-block h-1.5 w-1.5 rounded-full bg-current" />
      {label}: {verb}
    </span>
  );
}

/**
 * Clickable score badge with hover tooltip. Hovering reveals the per-category
 * breakdown (label, score, weight) so an analyst can read the composition
 * without leaving the page; clicking deep-links into the full score detail
 * view at /analyses/:id/score where each category includes the rationale
 * notes and threshold context.
 */
function ScoreBadgeLink({
  analysisId,
  scoreData,
}: {
  analysisId: string;
  scoreData: ScoreData;
}) {
  const breakdownEntries = Object.entries(scoreData.breakdown);
  return (
    <Link
      to={`/analyses/${analysisId}/score`}
      className="group relative inline-flex items-center"
      aria-label={`Project score ${scoreData.grade}, ${scoreData.score.toFixed(1)} out of 100. Click for details.`}
    >
      <span
        className="inline-flex items-center rounded-full px-3 py-1 text-sm font-bold text-white shadow-sm transition group-hover:shadow-md group-hover:brightness-110"
        style={{ backgroundColor: scoreData.gradeColor }}
      >
        {scoreData.grade}
        {scoreData.advisory && (
          <span className="ml-1.5 text-[10px] font-semibold uppercase tracking-wider opacity-90">
            · Advisory
          </span>
        )}
      </span>

      {/* Tooltip — appears on hover, click anywhere navigates to /score.
          The outer wrapper uses padding (not margin) for its visual offset
          from the badge so the mouse stays inside the hover region while
          crossing the gap — otherwise the tooltip closes before you can
          reach "View full breakdown". */}
      <div className="invisible absolute left-0 top-full z-30 w-72 pt-2 opacity-0 transition-all duration-150 group-hover:visible group-hover:opacity-100">
        <div
          role="tooltip"
          className="origin-top-left scale-95 rounded-xl border border-slate-200 bg-white p-3 shadow-xl transition-transform duration-150 group-hover:scale-100"
        >
          <div className="flex items-baseline justify-between border-b border-slate-100 pb-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Score breakdown
            </span>
            <span className="text-xs tabular-nums text-slate-700">
              {scoreData.score.toFixed(1)} / 100
            </span>
          </div>
          <ul className="mt-2 space-y-1 text-xs">
            {breakdownEntries.map(([key, cat]) => {
              const na = cat.notApplicable === true;
              const label =
                key === 'jobCreation' && scoreData.projectType === 'retention'
                  ? 'Employment Impact'
                  : ((
                      {
                        fiscalReturn: 'Fiscal Return',
                        jobCreation: 'Job Creation',
                        wageQuality: 'Wage Quality',
                        capitalInvestment: 'Capital Investment',
                        netFiscalImpact: 'Net Fiscal Impact',
                      } as Record<string, string>
                    )[key] ?? key);
              return (
                <li
                  key={key}
                  className={`flex items-baseline justify-between gap-3 ${
                    na ? 'text-slate-400' : 'text-slate-700'
                  }`}
                >
                  <span className="truncate">
                    {label}
                    {na && (
                      <span className="ml-1 rounded bg-slate-100 px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-slate-500">
                        N/A
                      </span>
                    )}
                  </span>
                  <span className="tabular-nums text-slate-500">
                    {na ? '—' : `${cat.score.toFixed(0)} · ${cat.weight.toFixed(0)}%`}
                  </span>
                </li>
              );
            })}
          </ul>
          <div className="mt-2 border-t border-slate-100 pt-2 text-center text-[11px] font-medium text-indigo-600">
            View full breakdown →
          </div>
        </div>
      </div>
    </Link>
  );
}

/**
 * Small "i" info-circle that reveals a plain-English help tooltip on hover
 * or keyboard focus. Used beside section headings so non-analyst readers
 * (board members, elected officials) can self-serve the "what does this
 * mean?" question without cluttering the layout with long paragraphs.
 *
 * Accessibility: the tooltip is wrapped in a focusable <button> so it's
 * keyboard-reachable; aria-describedby ties it to the tooltip body.
 */
function InfoTooltip({ label, children }: { label: string; children: React.ReactNode }) {
  const tipId = `tip-${label.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`;
  return (
    <span className="group relative inline-flex align-middle">
      <button
        type="button"
        aria-label={`About: ${label}`}
        aria-describedby={tipId}
        className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-slate-300 bg-white text-[10px] font-semibold text-slate-500 transition hover:border-indigo-400 hover:bg-indigo-50 hover:text-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-300"
      >
        i
      </button>
      {/* Padding-based offset (not margin) keeps the cursor inside the hover
          region while crossing from the icon to the tooltip — otherwise the
          tooltip closes before you can read it. */}
      <span
        role="tooltip"
        id={tipId}
        className="invisible absolute left-1/2 top-full z-30 w-72 -translate-x-1/2 pt-2 opacity-0 transition-all duration-150 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100"
      >
        <span className="block rounded-lg border border-slate-200 bg-white p-3 text-left text-xs leading-relaxed text-slate-600 shadow-xl">
          <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-slate-400">
            {label}
          </span>
          {children}
        </span>
      </span>
    </span>
  );
}

function KpiCard({ label, value, sub }: KpiCardProps) {
  return (
    <div className="rounded-2xl bg-white/60 backdrop-blur-xl border border-white/60 shadow-lg shadow-slate-900/5 p-5 transition hover:shadow-xl hover:shadow-slate-900/10">
      <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-slate-500">{label}</p>
      <p className="mt-2 font-display text-3xl leading-tight tabular-nums bg-gradient-to-br from-slate-900 to-slate-700 bg-clip-text text-transparent">
        {value}
      </p>
      {sub && <p className="mt-1 text-xs text-slate-500">{sub}</p>}
    </div>
  );
}

/** Build year-indexed data for Recharts from parallel arrays */
function buildYearSeries(arrays: Record<string, number[]>, length: number) {
  return Array.from({ length }, (_, i) => {
    const row: Record<string, number | string> = { year: `Yr ${i + 1}` };
    for (const [key, arr] of Object.entries(arrays)) {
      row[key] = arr[i] ?? 0;
    }
    return row;
  });
}

/** Year-by-year district fiscal impact table — glass card wrapper. */
function DistrictImpactTable({
  impact,
  entityMetrics,
}: {
  impact: DistrictImpact;
  entityMetrics?: { paybackPeriodYears: number | null; rateOfReturn: number | null };
}) {
  return (
    <div className="overflow-x-auto rounded-2xl bg-white/60 backdrop-blur-xl border border-white/60 shadow-lg shadow-slate-900/5 p-3">
      {entityMetrics && (
        <div className="mb-2 flex flex-wrap gap-x-4 gap-y-1 px-1 text-xs text-slate-500">
          <span>
            Payback:{' '}
            <span className="font-medium text-slate-700">
              {fmtPaybackYears(entityMetrics.paybackPeriodYears, entityMetrics.rateOfReturn)}
            </span>
          </span>
          <span>
            ROI:{' '}
            <span className="font-medium text-slate-700">
              {entityMetrics.rateOfReturn !== null
                ? `${(entityMetrics.rateOfReturn * 100).toFixed(1)}%`
                : 'N/A'}
            </span>
          </span>
        </div>
      )}
      <table className="min-w-full border-collapse text-sm tabular-nums">
        <thead>
          <tr className="border-b-2 border-slate-900">
            <th className="px-3 py-2 text-left text-[11px] font-medium uppercase tracking-[0.12em] text-slate-500">
              Year
            </th>
            <th className="px-3 py-2 text-right text-[11px] font-medium uppercase tracking-[0.12em] text-slate-500">
              Property Tax
            </th>
            <th className="px-3 py-2 text-right text-[11px] font-medium uppercase tracking-[0.12em] text-slate-500">
              Service Costs
            </th>
            <th className="px-3 py-2 text-right text-[11px] font-medium uppercase tracking-[0.12em] text-slate-500">
              Net Benefit
            </th>
            <th className="px-3 py-2 text-right text-[11px] font-medium uppercase tracking-[0.12em] text-slate-500">
              Cumulative
            </th>
          </tr>
        </thead>
        <tbody>
          {impact.annualNetBenefits.map((_, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: year index is stable
            <tr key={i} className="border-b border-slate-100">
              <td className="px-3 py-2 text-slate-500">Year {i + 1}</td>
              <td className="px-3 py-2 text-right text-slate-700">
                {fmtCurrency(impact.propertyTax[i] ?? 0)}
              </td>
              <td className="px-3 py-2 text-right text-develio-orange">
                {fmtCurrency(impact.serviceCosts[i] ?? 0)}
              </td>
              <td
                className={`px-3 py-2 text-right font-medium ${(impact.annualNetBenefits[i] ?? 0) >= 0 ? 'text-brand-green-700' : 'text-develio-orange'}`}
              >
                {fmtCurrency(impact.annualNetBenefits[i] ?? 0)}
              </td>
              <td className="px-3 py-2 text-right text-slate-600">
                {fmtCurrency(impact.cumulativeNetBenefits[i] ?? 0)}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t-2 border-slate-900">
            <td className="px-3 py-2 font-display text-base text-slate-900">Total</td>
            <td colSpan={2} />
            <td
              className={`px-3 py-2 text-right font-display text-base ${impact.tenYearNetBenefits >= 0 ? 'text-brand-green-700' : 'text-develio-orange'}`}
            >
              {fmtCurrency(impact.tenYearNetBenefits)}
            </td>
            <td />
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

/** Simple CSS-width bar showing a single percentile row */
/**
 * Horizontal "range bar" for a single Monte Carlo percentile spread.
 *
 * Renders a single axis where the bar spans P10 → P90 (the 80% confidence
 * interval from the simulation) with a visible tick at P50 (the median).
 * This is a much clearer mental model than three separate zero-anchored
 * bars: readers see ONE distribution's *spread* at a glance, with the
 * "most likely" outcome marked in the middle.
 *
 * Axis padding — we don't anchor the axis at zero because when the
 * distribution is tight (e.g. P10=$2.4M, P90=$2.6M), a zero-anchored bar
 * would collapse to a nearly invisible slice. Instead the axis spans
 * [P10 - 10% range, P90 + 10% range] so there's always visible context
 * on either side of the confidence interval.
 */
function PercentileRangeBar({
  p10,
  p50,
  p90,
}: {
  p10: number;
  p50: number;
  p90: number;
}) {
  // Add 10% padding on each side of the P10..P90 range for the axis scale,
  // so the interval bar doesn't run flush to the edges. Fall back to a
  // minimum span when the range collapses to zero (e.g. no uncertainty).
  const range = Math.max(p90 - p10, 1);
  const pad = range * 0.1;
  const axisMin = p10 - pad;
  const axisMax = p90 + pad;
  const toPct = (v: number) => ((v - axisMin) / (axisMax - axisMin)) * 100;
  const leftPct = toPct(p10);
  const widthPct = toPct(p90) - leftPct;
  const p50Pct = toPct(p50);

  return (
    <div>
      <div className="relative h-8">
        {/* Axis track */}
        <div className="absolute left-0 right-0 top-1/2 h-1.5 -translate-y-1/2 rounded-full bg-slate-100" />
        {/* P10..P90 confidence interval */}
        <div
          className="absolute top-1/2 h-3 -translate-y-1/2 rounded-full bg-indigo-200"
          style={{ left: `${leftPct}%`, width: `${widthPct}%` }}
          title={`80% chance the outcome lands between ${fmtK(p10)} and ${fmtK(p90)}`}
        />
        {/* P50 median marker */}
        <div
          className="absolute top-1/2 h-5 w-1 -translate-x-1/2 -translate-y-1/2 rounded-sm bg-indigo-700"
          style={{ left: `${p50Pct}%` }}
          title={`Median outcome (P50): ${fmtK(p50)}`}
        />
      </div>
      {/* Value labels positioned under the three markers */}
      <div className="relative mt-1 text-[11px] tabular-nums">
        <div className="absolute -translate-x-1/2 text-slate-500" style={{ left: `${leftPct}%` }}>
          <div>{fmtK(p10)}</div>
          <div className="text-[9px] uppercase tracking-wider text-slate-400">P10</div>
        </div>
        <div
          className="absolute -translate-x-1/2 text-center font-semibold text-slate-800"
          style={{ left: `${p50Pct}%` }}
        >
          <div>{fmtK(p50)}</div>
          <div className="text-[9px] uppercase tracking-wider text-indigo-600">Median</div>
        </div>
        <div
          className="absolute -translate-x-1/2 text-right text-slate-500"
          style={{ left: `${toPct(p90)}%` }}
        >
          <div>{fmtK(p90)}</div>
          <div className="text-[9px] uppercase tracking-wider text-slate-400">P90</div>
        </div>
        {/* Reserve vertical room for the absolutely-positioned labels above. */}
        <div className="invisible">
          <div>placeholder</div>
          <div className="text-[9px]">P</div>
        </div>
      </div>
    </div>
  );
}

function MonteCarloSection({ mc }: { mc: MonteCarloStats }) {
  const tnb = mc.tenYearNetBenefits;
  const npv = mc.netPresentValue;
  return (
    <section className="space-y-4">
      <div className="mb-4">
        <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-slate-500">
          Uncertainty
        </p>
        <h3 className="font-display text-2xl leading-tight text-slate-900 inline-flex items-center gap-2">
          Monte Carlo Distribution
          <InfoTooltip label="Monte Carlo Distribution">
            We run the analysis thousands of times, randomly varying the most uncertain inputs
            (construction cost, job counts, wages, tax rates) within realistic ranges. The result is
            a <strong>range of plausible outcomes</strong>, not a single number. The dark tick is
            the median (most likely), and the shaded band is the 80% confidence range. This helps
            you reason about <em>how confident</em> you should be in the projection.
          </InfoTooltip>
        </h3>
      </div>
      <div className="rounded-2xl bg-white/60 backdrop-blur-xl border border-white/60 shadow-lg shadow-slate-900/5 p-5 space-y-8">
        {/* Plain-English legend — the old label-per-row bar chart confused
            readers because it implied three independent quantities instead
            of one spread. Explain what the markers mean up front. */}
        <p className="text-xs text-slate-500">
          The dark bar marks the <span className="font-semibold text-slate-700">most likely</span>{' '}
          outcome. The shaded band shows the{' '}
          <span className="font-semibold text-slate-700">80% confidence range</span> — there&rsquo;s
          roughly a 10% chance the real outcome lands below the left edge, and a 10% chance it
          exceeds the right edge.
        </p>

        <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
          {/* Net Benefits distribution */}
          <div>
            <p className="mb-4 text-sm font-medium text-slate-700">Net Benefits (10-Year)</p>
            <PercentileRangeBar p10={tnb.p10} p50={tnb.p50} p90={tnb.p90} />
            <p className="mt-4 text-xs text-slate-400">
              Mean {fmtK(tnb.mean)} · Std dev ±{fmtK(tnb.stdDev)}
            </p>
          </div>

          {/* NPV distribution */}
          <div>
            <p className="mb-4 text-sm font-medium text-slate-700">Net Present Value (10-Year)</p>
            <PercentileRangeBar p10={npv.p10} p50={npv.p50} p90={npv.p90} />
            <p className="mt-4 text-xs text-slate-400">
              Mean {fmtK(npv.mean)} · Std dev ±{fmtK(npv.stdDev)}
            </p>
          </div>
        </div>

        <p className="text-xs text-slate-400 border-t border-slate-100 pt-3">
          {mc.iterations.toLocaleString()} iterations · ±15% capex, ±10% jobs/wage, ±5% tax rate
        </p>
      </div>
    </section>
  );
}

/**
 * Tornado chart: one row per parameter, bar spanning from lowOutput to
 * highOutput, centered on baseMetricValue. Bars to the LEFT of base indicate
 * the "low" side of the sweep produces a smaller output; bars to the RIGHT
 * indicate the "high" side produces a larger output. Rows are sorted by
 * |max delta| descending — the largest swings sit at the top.
 */
function SensitivitySection({ sens }: { sens: SensitivityStats }) {
  const rows = sens.parameters;
  if (rows.length === 0) {
    return (
      <section className="space-y-4">
        <div className="mb-4">
          <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-slate-500">
            Sensitivity
          </p>
          <h3 className="font-display text-2xl leading-tight text-slate-900 inline-flex items-center gap-2">
            Sensitivity Analysis
            <InfoTooltip label="Sensitivity Analysis">
              Which assumptions move the needle the most? Each bar shows how much the 10-year net
              benefit changes when a single input is nudged up and down by 20%, holding everything
              else constant. The parameters at the <strong>top</strong> are the ones your analysis
              is <strong>most sensitive to</strong> — those are the numbers worth double-checking.
            </InfoTooltip>
          </h3>
        </div>
        <div className="rounded-2xl bg-white/60 backdrop-blur-xl border border-white/60 shadow-lg shadow-slate-900/5 p-5 text-sm text-slate-500">
          No parameters had non-zero base values to sweep.
        </div>
      </section>
    );
  }

  // The half-range that determines the chart's horizontal scale. Every bar is
  // rendered relative to this span so the largest swing fills the row.
  const maxAbsDelta = rows[0]?.absMaxDelta ?? 1;
  const base = sens.baseMetricValue;

  return (
    <section className="space-y-4">
      <div className="mb-4">
        <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-slate-500">
          Sensitivity
        </p>
        <h3 className="font-display text-2xl leading-tight text-slate-900 inline-flex items-center gap-2">
          Tornado Chart
          <InfoTooltip label="Tornado Chart">
            Ranks project assumptions by how much they move the 10-year net benefit when pushed
            ±20%. Wider bars at the top = higher-leverage inputs — focus your due diligence there.
            The shape resembles a tornado because the highest-impact items sit at the top and taper
            downward.
          </InfoTooltip>
        </h3>
      </div>
      <div className="rounded-2xl bg-white/60 backdrop-blur-xl border border-white/60 shadow-lg shadow-slate-900/5 p-5">
        <p className="mb-4 text-xs text-slate-500">
          Each bar shows how ±{Math.round((rows[0]?.perturbationPct ?? 0.2) * 100)}% change in a
          single assumption moves 10-year net benefits (base: {fmtK(base)}). Parameters are sorted
          by largest swing first.
        </p>
        <div className="space-y-2">
          {rows.map((row) => {
            // Position the low/high ends relative to base, normalized to maxAbsDelta.
            // A bar extends from min(lowOutput, highOutput) to max(...) in output space.
            const lowRel = row.lowOutput - base; // can be negative or positive
            const highRel = row.highOutput - base;
            const left = Math.min(lowRel, highRel);
            const right = Math.max(lowRel, highRel);
            // Map to percentage positions in a [−maxAbsDelta, +maxAbsDelta] horizontal scale.
            const toPct = (v: number) => 50 + (v / maxAbsDelta) * 50;
            const leftPct = toPct(left);
            const rightPct = toPct(right);
            const widthPct = Math.max(rightPct - leftPct, 0.5); // guarantee visible sliver

            return (
              <div
                key={row.parameter}
                className="grid grid-cols-[180px_1fr_120px] items-center gap-3"
              >
                <div className="truncate text-xs font-medium text-slate-700">
                  {SENSITIVITY_PARAM_LABELS[row.parameter] ?? row.parameter}
                </div>
                <div className="relative h-6 rounded bg-slate-50">
                  {/* Center zero line */}
                  <div
                    className="absolute top-0 bottom-0 w-px bg-slate-300"
                    style={{ left: '50%' }}
                  />
                  {/* The bar */}
                  <div
                    className="absolute top-1 bottom-1 rounded bg-indigo-500/70"
                    style={{ left: `${leftPct}%`, width: `${widthPct}%` }}
                    title={`Low: ${fmtK(row.lowOutput)} · High: ${fmtK(row.highOutput)}`}
                  />
                </div>
                <div className="text-right text-xs tabular-nums text-slate-500">
                  ±{fmtK(row.absMaxDelta)}
                </div>
              </div>
            );
          })}
        </div>
        <p className="mt-4 border-t border-slate-100 pt-3 text-xs text-slate-400">
          One-at-a-time ±20% deterministic sweep · {rows.length} parameters
        </p>
      </div>
    </section>
  );
}

export function AnalysisResultRoute() {
  const { id } = useParams<{ id: string }>();
  const getToken = useSessionTokenGetter();
  const navigate = useNavigate();
  const org = useCurrentOrg();
  const isReviewer = org?.role === 'reviewer';
  const [analysis, setAnalysis] = useState<Analysis | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Separate loading flags per variant so clicking one button only greys out
  // that button. Without this both buttons share state and look like they're
  // each triggering work — they aren't (only the clicked variant is fetched).
  const [wageAnalysis, setWageAnalysis] = useState<WageAnalysisResult | null>(null);
  const [fullPdfLoading, setFullPdfLoading] = useState(false);
  const [summaryPdfLoading, setSummaryPdfLoading] = useState(false);
  const [xlsxLoading, setXlsxLoading] = useState(false);
  const [districtTab, setDistrictTab] = useState<'city' | 'county' | 'isd' | 'special'>('city');
  // ADR-033 Part C: "Recompute with latest state data" rebuilds step 6 from
  // the org's current default profile and re-runs. reloadKey re-triggers the
  // analysis fetch so the page reflects the new version without a full reload.
  const [reloadKey, setReloadKey] = useState(0);
  const [recomputing, setRecomputing] = useState(false);
  const [recomputeError, setRecomputeError] = useState<string | null>(null);
  // MC/sensitivity auto-run: the manual buttons are gone — both jobs fire
  // automatically from /calculate. We still poll the analysis row so the
  // tornado/distribution sections populate live. See the JobStatusChip
  // in the header and the inline status banners below the fiscal summary.

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${API}/api/v1/analyses/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
        const json = await res.json();
        if (!cancelled) setAnalysis(json.analysis as Analysis);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, getToken, reloadKey]);

  // Fetch wage quality on demand once the analysis loads and has results.
  useEffect(() => {
    if (!analysis?.id || !analysis.results) return;
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${API}/api/v1/analyses/${analysis.id}/wage-analysis`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok && !cancelled) setWageAnalysis((await res.json()) as WageAnalysisResult);
      } catch {
        // non-fatal — wage panel silently absent
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [analysis?.id, analysis?.results, getToken]);

  // Auto-poll for MC + sensitivity while either job is queued/running.
  // Both fire from /calculate; the row's *_status column tells us whether to
  // keep polling or stop. We intentionally keep polling while status is
  // 'idle' for a brief window after mount — Inngest can lag a beat before
  // the row flips to 'queued', and old analyses (pre-auto-run) will just
  // sit at 'idle' forever (harmless).
  useEffect(() => {
    if (!analysis?.id) return;
    const mcDone =
      analysis.monteCarloResults != null ||
      analysis.monteCarloStatus === 'ready' ||
      analysis.monteCarloStatus === 'failed';
    const sensDone =
      analysis.sensitivityResults != null ||
      analysis.sensitivityStatus === 'ready' ||
      analysis.sensitivityStatus === 'failed';
    if (mcDone && sensDone) return;

    let cancelled = false;
    const deadline = Date.now() + 120_000; // stop after 2 min to avoid runaway polling
    const timer = setInterval(async () => {
      if (cancelled || Date.now() > deadline) {
        clearInterval(timer);
        return;
      }
      try {
        const token = await getToken();
        const res = await fetch(`${API}/api/v1/analyses/${analysis.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const json = (await res.json()) as { analysis: Analysis };
        if (cancelled) return;
        setAnalysis(json.analysis);
        const nextMcDone =
          json.analysis.monteCarloResults != null ||
          json.analysis.monteCarloStatus === 'ready' ||
          json.analysis.monteCarloStatus === 'failed';
        const nextSensDone =
          json.analysis.sensitivityResults != null ||
          json.analysis.sensitivityStatus === 'ready' ||
          json.analysis.sensitivityStatus === 'failed';
        if (nextMcDone && nextSensDone) clearInterval(timer);
      } catch {
        // non-fatal poll error — keep trying until deadline
      }
    }, 3_000);

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [
    analysis?.id,
    analysis?.monteCarloResults,
    analysis?.sensitivityResults,
    analysis?.monteCarloStatus,
    analysis?.sensitivityStatus,
    getToken,
  ]);

  async function handleRecompute() {
    if (!analysis) return;
    setRecomputing(true);
    setRecomputeError(null);
    try {
      const token = await getToken();
      const res = await fetch(`${API}/api/v1/analyses/${analysis.id}/recompute`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? `Recompute failed (${res.status})`);
      }
      // Re-fetch so the page shows the new version's results + score.
      setReloadKey((k) => k + 1);
    } catch (e) {
      setRecomputeError(e instanceof Error ? e.message : 'Recompute failed');
    } finally {
      setRecomputing(false);
    }
  }

  async function downloadXlsx() {
    if (!analysis) return;
    setXlsxLoading(true);
    try {
      const token = await getToken();
      const res = await fetch(`${API}/api/v1/analyses/${analysis.id}/xlsx`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Excel generation failed: ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${analysis.projectName.replace(/[^a-z0-9]/gi, '_')}_impact_report.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      analytics.capture('xlsx.downloaded', {
        analysisId: analysis.id,
        projectName: analysis.projectName,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setXlsxLoading(false);
    }
  }

  async function downloadPdf(options?: { refresh?: boolean; variant?: 'full' | 'summary' }) {
    if (!analysis) return;
    const isSummary = options?.variant === 'summary';
    if (isSummary) setSummaryPdfLoading(true);
    else setFullPdfLoading(true);
    try {
      const token = await getToken();
      const params = new URLSearchParams();
      if (options?.refresh) params.set('refresh', 'true');
      if (options?.variant === 'summary') params.set('variant', 'summary');
      const qs = params.toString() ? `?${params.toString()}` : '';
      const res = await fetch(`${API}/api/v1/analyses/${analysis.id}/pdf${qs}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`PDF generation failed: ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const dateStr = new Date().toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
      const cleanProject = analysis.projectName.replace(/[\\/:*?"<>|]/g, '').trim() || 'Analysis';
      const label = options?.variant === 'summary' ? 'Summary' : 'Analysis';
      a.download = `${cleanProject} ${label} - ${dateStr}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      analytics.capture('pdf.downloaded', {
        analysisId: analysis.id,
        projectName: analysis.projectName,
        refresh: options?.refresh ?? false,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      if (isSummary) setSummaryPdfLoading(false);
      else setFullPdfLoading(false);
    }
  }

  const r = analysis?.results ?? null;

  // Build available district tabs based on which impacts are present.
  // MUST be called before any early returns — see React error #310.
  const availableTabs = useMemo(() => {
    const tabs: { key: 'city' | 'county' | 'isd' | 'special'; label: string }[] = [
      { key: 'city', label: 'City' },
    ];
    if (r?.countyImpact) tabs.push({ key: 'county', label: 'County' });
    if (r?.isdImpact) tabs.push({ key: 'isd', label: 'ISD' });
    if (r?.specialDistrictImpact) tabs.push({ key: 'special', label: 'Special District' });
    return tabs;
  }, [r]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <p className="text-sm text-slate-500">Loading analysis…</p>
      </div>
    );
  }

  if (error || !analysis) {
    return (
      <div className="space-y-4">
        <Link to="/analyses" className="text-sm text-slate-500 hover:underline">
          ← Back to analyses
        </Link>
        <p className="text-sm text-red-600">{error ?? 'Analysis not found.'}</p>
      </div>
    );
  }

  const years = r?.cityImpact?.annualNetBenefits?.length ?? 10;

  // Extract validation bypass report from inputs if present
  const validationReport =
    (
      analysis.inputs as {
        _validationBypasses?: { bypasses: unknown[]; validatedAt: string };
      } | null
    )?._validationBypasses ?? null;

  // 10-year line chart data: jobs + net benefits
  const trendData = r
    ? buildYearSeries(
        {
          'Total Jobs': r.projections.effectiveTotalJobs,
          'Net Benefits': r.cityImpact.annualNetBenefits,
          'Cumulative Net': r.cityImpact.cumulativeNetBenefits,
        },
        years,
      )
    : [];

  // Fiscal waterfall: benefits breakdown by category (year-10 or stabilized)
  const waterfallYear = years - 1;
  const waterfallData = r
    ? [
        { name: 'Sales Tax', value: r.cityImpact.salesTaxProjectNetNew[waterfallYear] ?? 0 },
        { name: 'Property Tax', value: r.cityImpact.realPropertyTax[waterfallYear] ?? 0 },
        { name: 'BPP Tax', value: r.cityImpact.bppPropertyTax[waterfallYear] ?? 0 },
        { name: 'HOT', value: r.cityImpact.hotelOccupancyTax[waterfallYear] ?? 0 },
        { name: 'Utility Fees', value: r.cityImpact.utilityFranchiseFees[waterfallYear] ?? 0 },
        { name: 'Misc Fees', value: r.cityImpact.miscTaxesAndFees[waterfallYear] ?? 0 },
        { name: 'Cost of Svcs', value: -(r.cityImpact.costsSubtotal[waterfallYear] ?? 0) },
      ].filter((d) => d.value !== 0)
    : [];

  const overrideCount = analysis.overridesJson?.fields?.length ?? 0;
  const waiver = analysis.overridesJson?.consultantWaiver;
  const waiverShortId = waiver?.authorizationRecordId.slice(-8).toUpperCase();

  return (
    <div className="space-y-8">
      {waiver && (
        <div className="flex items-center gap-3 rounded-full border border-indigo-200 bg-indigo-50 px-4 py-2 text-xs text-indigo-900">
          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-indigo-600 text-[10px] font-bold text-white">
            C
          </span>
          <div>
            <span className="font-semibold">
              Prepared by consultant — methodology overrides documented under waiver #
              {waiverShortId}.
            </span>
            {waiver.signedAt && (
              <span className="ml-2 text-indigo-700">
                Signed {new Date(waiver.signedAt).toLocaleDateString()}
                {waiver.signerName ? ` by ${waiver.signerName}` : ''}
              </span>
            )}
          </div>
        </div>
      )}
      {overrideCount > 0 && analysis.governanceEnabled && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <div className="font-semibold">
            Methodology override ({overrideCount} field{overrideCount === 1 ? '' : 's'})
          </div>
          <p className="mt-1 text-xs">
            This analysis was run by an{' '}
            {analysis.overridesJson?.role === 'consultant' ? 'authorized consultant' : 'owner'} who
            knowingly diverged from the organization's locked methodology profile. The override and
            reason are disclosed on the final PDF report.
            {analysis.overridesJson?.reason && (
              <>
                {' '}
                Reason: <i>{analysis.overridesJson.reason}</i>
              </>
            )}
          </p>
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <Link to="/analyses" className="text-xs text-slate-400 hover:underline">
            ← Analyses
          </Link>
          <div className="mt-1 flex items-center gap-3">
            <h2 className="text-2xl font-semibold tracking-tight">{analysis.projectName}</h2>
            {analysis.scoreData && (
              <ScoreBadgeLink analysisId={analysis.id} scoreData={analysis.scoreData} />
            )}
            {analysis.scoreData?.projectType && (
              <span className="inline-flex items-center rounded-md border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs font-medium text-slate-600">
                {PROJECT_TYPE_LABELS[analysis.scoreData.projectType]}
              </span>
            )}
            {/* Background-job status chips. Hidden when everything is ready
                or when the analysis predates auto-run ('idle'). */}
            <JobStatusChip
              label="Monte Carlo"
              status={analysis.monteCarloStatus}
              hasResults={!!analysis.monteCarloResults}
            />
            <JobStatusChip
              label="Sensitivity"
              status={analysis.sensitivityStatus}
              hasResults={!!analysis.sensitivityResults}
            />
          </div>
          <p className="mt-0.5 text-sm text-slate-500">
            Status: <span className="capitalize">{analysis.status.replace('_', ' ')}</span>
            {' · '}
            {new Date(analysis.createdAt).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })}
          </p>
        </div>
        <div className="flex gap-2">
          <Link
            to={`/analyses/${analysis.id}/scenarios`}
            className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Scenarios
          </Link>
          {!isReviewer && (
            <button
              type="button"
              onClick={() => navigate('/analyses/new', { state: { rerunFrom: analysis.inputs } })}
              className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Edit & Re-run
            </button>
          )}
          {!isReviewer && analysis.results && (
            <button
              type="button"
              onClick={() => void handleRecompute()}
              disabled={recomputing}
              title="Rebuild jurisdiction & tax rates (step 6) from your current default profile, then re-run"
              className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              {recomputing ? 'Recomputing…' : 'Recompute with latest state data'}
            </button>
          )}
          {analysis.results && (
            <>
              <button
                type="button"
                onClick={() => void downloadPdf()}
                disabled={fullPdfLoading}
                className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                {fullPdfLoading ? 'Generating…' : 'Full Report'}
              </button>
              <button
                type="button"
                onClick={() => void downloadPdf({ variant: 'summary' })}
                disabled={summaryPdfLoading}
                title="Single-page board-ready summary"
                className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                {summaryPdfLoading ? 'Generating…' : 'One-page Summary'}
              </button>
            </>
          )}
          {analysis.results && analytics.isFeatureEnabled('excel-export-enabled') && (
            <button
              type="button"
              onClick={() => void downloadXlsx()}
              disabled={xlsxLoading}
              className="rounded-md border border-emerald-300 bg-emerald-50 px-4 py-2 text-sm font-medium text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
            >
              {xlsxLoading ? 'Preparing…' : 'Download Excel'}
            </button>
          )}
          <Link
            to={`/print/analyses/${analysis.id}`}
            target="_blank"
            rel="noreferrer"
            className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Print view
          </Link>
        </div>
      </div>

      {recomputeError && (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {recomputeError}
        </div>
      )}

      {/* ADR-032: advisory banner for project types with directional scoring */}
      {analysis.scoreData?.advisory && analysis.scoreData.advisoryReason && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <strong className="font-semibold uppercase tracking-wide text-xs">Advisory:</strong>{' '}
          {analysis.scoreData.advisoryReason}
        </div>
      )}

      {!r ? (
        <div className="rounded-lg border border-dashed border-slate-300 bg-white p-8 text-center">
          <p className="text-sm text-slate-500">This analysis is a draft. Run it to see results.</p>
        </div>
      ) : (
        <>
          {/* Plain-language verdict — leads the report so a non-technical
              reader gets the "is this a good deal?" answer before the
              numbers. Tone is positive (green) or caution (amber), never an
              error: a net-cost result is a valid finding. */}
          {(() => {
            const v = fiscalVerdict(
              r.summary,
              r.projections.stabilizedTotalJobs,
              analysis.scoreData?.projectType === 'retention',
            );
            const tone =
              v.tone === 'positive'
                ? 'border-green-200 bg-green-50 text-green-900'
                : 'border-amber-200 bg-amber-50 text-amber-900';
            return (
              <div className={`flex items-start gap-3 rounded-lg border px-4 py-3 ${tone}`}>
                <span className="mt-0.5 shrink-0" aria-hidden="true">
                  {v.tone === 'positive' ? '✓' : '⚠'}
                </span>
                <div>
                  <p className="text-sm font-semibold">{v.title}</p>
                  <p className="mt-0.5 text-sm">{v.detail}</p>
                </div>
              </div>
            );
          })()}

          {/* Incentive package summary — at-a-glance view of what's offered
              and what it costs. Hidden when no levers are active. Levers
              and totals come from the same shared helper as the PDF, so
              the two views never drift. */}
          <IncentivesSummaryBox
            inputs={analysis.inputs}
            summary={{
              totalIncentive: r.summary.totalIncentive,
              perJobCost: r.summary.perJobIncentiveCost,
              rateOfReturn: r.summary.rateOfReturn,
              paybackYears: r.summary.paybackPeriodYears,
            }}
          />

          {/* Inline scenarios preview — answers "is this analysis being
              scenario-tested, and what direction are the variations going?"
              at a glance. Full side-by-side compare lives at
              /analyses/:id/compare. */}
          <ScenariosPreviewPanel
            analysisId={analysis.id}
            baseSummary={{
              tenYearNetBenefits: r.summary.tenYearNetBenefits ?? null,
              totalIncentive: r.summary.totalIncentive ?? null,
              paybackPeriodYears: r.summary.paybackPeriodYears ?? null,
            }}
          />

          {/* Version history — collapsible panel listing every analysis_versions
              row with a download-PDF link per version. Hides itself when there's
              only one version (the common case). */}
          <VersionHistoryPanel analysisId={analysis.id} projectName={analysis.projectName} />

          {/* Summary KPIs — ADR-031: retention flips labels from "benefit"
              to "avoided loss"; the math is identical, the framing isn't. */}
          <section>
            <h3 className="mb-4 font-display text-2xl leading-tight text-slate-900 inline-flex items-center gap-2">
              {analysis.scoreData?.projectType === 'retention'
                ? '10-Year Avoided Loss'
                : '10-Year Fiscal Summary'}
              <InfoTooltip
                label={
                  analysis.scoreData?.projectType === 'retention'
                    ? '10-Year Avoided Loss'
                    : '10-Year Fiscal Summary'
                }
              >
                The headline numbers over a 10-year horizon. <strong>Net benefit</strong> is total
                city revenue generated by the project minus the cost of services to support it.{' '}
                <strong>Present value</strong> discounts future dollars back to today so you can
                compare them on equal footing. <strong>Payback period</strong> is the year the
                project&rsquo;s cumulative net benefits first cover the incentive cost.
              </InfoTooltip>
            </h3>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <KpiCard
                label={
                  analysis.scoreData?.projectType === 'retention'
                    ? 'City avoided loss (10 yr)'
                    : 'Net city benefit (10 yr)'
                }
                value={fmtCurrency(r.summary.tenYearNetBenefits)}
                sub="Nominal"
              />
              <KpiCard
                label="Present value (10 yr)"
                value={fmtCurrency(r.summary.tenYearPresentValue)}
                sub="Discounted"
              />
              <KpiCard
                label={
                  analysis.scoreData?.projectType === 'retention'
                    ? 'Grand total avoided loss'
                    : 'Grand total city benefit'
                }
                value={fmtCurrency(r.summary.grandTotalCityBenefit)}
                sub="Operations + construction"
              />
              {r.summary.paybackPeriodYears !== null ? (
                <KpiCard
                  label="Payback period"
                  value={`${fmtNum(r.summary.paybackPeriodYears)} yrs`}
                  sub="Years to recover incentive"
                />
              ) : (
                <KpiCard label="Payback period" value="N/A" sub="No incentive or never recoups" />
              )}
            </div>
          </section>

          {/* Employment KPIs */}
          <section>
            <h3 className="mb-4 font-display text-2xl leading-tight text-slate-900 inline-flex items-center gap-2">
              Employment
              <InfoTooltip label="Employment">
                Jobs created (or retained) by the project at full operations.{' '}
                <strong>Direct jobs</strong> are hired by the company itself;{' '}
                <strong>spin-off jobs</strong> include indirect (supplier) and induced (local
                spending by new workers) employment, estimated from BEA RIMS II Type II multipliers.{' '}
                <strong>Cost per job</strong> divides the total incentive by stabilized jobs — a
                common efficiency benchmark.
              </InfoTooltip>
            </h3>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <KpiCard
                label="Stabilized total jobs"
                value={fmtNum(r.projections.stabilizedTotalJobs)}
                sub="Direct + spin-off"
              />
              <KpiCard
                label="Stabilized spin-off jobs"
                value={fmtNum(r.projections.stabilizedSpinOffJobs)}
                sub="Indirect + induced"
              />
              <KpiCard
                label="Construction jobs"
                value={fmtNum(r.construction.constructionJobs)}
                sub="Total supported"
              />
              <KpiCard
                label="Cost per job"
                value={
                  r.summary.perJobIncentiveCost > 0
                    ? fmtCurrency(r.summary.perJobIncentiveCost)
                    : '—'
                }
                sub="Incentive ÷ stabilized jobs"
              />
            </div>
            <BEAAttribution variant="rims" className="mt-3" />
          </section>

          {/* Wage Quality — F4 integration. Silently absent when no OES data or missing inputs. */}
          {wageAnalysis?.wageAnalysis && (
            <section>
              <h3 className="mb-4 font-display text-2xl leading-tight text-slate-900 inline-flex items-center gap-2">
                Wage Quality
                <InfoTooltip label="Wage Quality">
                  Compares the project's implied average wage (annual payroll ÷ direct jobs) to the
                  BLS OES local median for the closest occupational group. The badge shows where it
                  falls: <strong>Below Market</strong> (&lt;85%), <strong>At Market</strong>{' '}
                  (85–115%), <strong>Above Market</strong> (115–140%), or <strong>Premium</strong>{' '}
                  (&gt;140%).
                </InfoTooltip>
              </h3>
              <div className="rounded-2xl bg-white/60 backdrop-blur-xl border border-white/60 shadow-lg shadow-slate-900/5 p-5">
                <div className="flex flex-wrap items-center gap-6">
                  <div className="flex items-center gap-3">
                    <span
                      className={`rounded-full border px-4 py-1.5 text-sm font-bold ${WAGE_BADGE_CLASSES[wageAnalysis.wageAnalysis.badge] ?? 'bg-slate-100 text-slate-700 border-slate-300'}`}
                    >
                      {wageAnalysis.wageAnalysis.badgeLabel}
                    </span>
                    <span className="text-2xl font-display text-slate-800 tabular-nums">
                      {(wageAnalysis.wageAnalysis.ratio * 100).toFixed(0)}%
                    </span>
                    <span className="text-sm text-slate-500">of local median</span>
                  </div>
                  <div className="flex gap-6 text-sm">
                    <div>
                      <p className="text-[11px] uppercase tracking-wide text-slate-400">
                        Implied avg wage
                      </p>
                      <p className="font-semibold text-slate-800">
                        {fmtCurrency(wageAnalysis.wageAnalysis.impliedAnnualWage)}
                      </p>
                    </div>
                    <div>
                      <p className="text-[11px] uppercase tracking-wide text-slate-400">
                        Local median
                      </p>
                      <p className="font-semibold text-slate-800">
                        {fmtCurrency(wageAnalysis.wageAnalysis.medianWage)}
                      </p>
                    </div>
                    <div>
                      <p className="text-[11px] uppercase tracking-wide text-slate-400">
                        Occupation
                      </p>
                      <p className="font-semibold text-slate-800">
                        {wageAnalysis.wageAnalysis.local.occupationTitle}
                      </p>
                      <p className="text-[11px] text-slate-400">
                        {wageAnalysis.wageAnalysis.local.areaName}
                      </p>
                    </div>
                  </div>
                </div>
                <p className="mt-4 text-[11px] text-slate-400">
                  {wageAnalysis.wageAnalysis.citation}
                </p>
              </div>
            </section>
          )}

          {/* Incentive KPIs */}
          <section>
            <h3 className="mb-4 font-display text-2xl leading-tight text-slate-900 inline-flex items-center gap-2">
              Incentive Analysis
              <InfoTooltip label="Incentive Analysis">
                The public cost of the deal. <strong>Total incentive cost</strong> is what the city
                spends (grants, abatements, fee waivers) over the full program term.{' '}
                <strong>Rate of return</strong> is net city benefits divided by incentive cost — a
                number above 100% means the city nets more than it spends.
              </InfoTooltip>
            </h3>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              <KpiCard
                label="Total incentive cost"
                value={fmtCurrency(r.summary.totalIncentive)}
                sub="To city over program term"
              />
              <KpiCard
                label="Rate of return"
                value={
                  r.summary.rateOfReturn !== null
                    ? `${(r.summary.rateOfReturn * 100).toFixed(1)}%`
                    : 'N/A'
                }
                sub="Net benefits ÷ incentive"
              />
              <KpiCard
                label="Construction city revenue"
                value={fmtCurrency(r.construction.totalConstructionCityRevenue)}
                sub="Permits, materials tax, worker spending"
              />
            </div>
          </section>

          {/* Monte Carlo Distribution — auto-run from /calculate. Banner reflects status. */}
          {!analysis.monteCarloResults &&
            (analysis.monteCarloStatus === 'queued' || analysis.monteCarloStatus === 'running') && (
              <section>
                <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-4 text-sm text-indigo-700">
                  Monte Carlo simulation{' '}
                  {analysis.monteCarloStatus === 'running' ? 'running' : 'queued'} — results will
                  appear shortly.
                </div>
              </section>
            )}
          {analysis.monteCarloStatus === 'failed' && !analysis.monteCarloResults && (
            <section>
              <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                Monte Carlo simulation failed. Re-run the analysis (Edit &amp; Re-run) to try again.
              </div>
            </section>
          )}
          {analysis.monteCarloResults && <MonteCarloSection mc={analysis.monteCarloResults} />}

          {/* Sensitivity / tornado chart — ADR-028, auto-run from /calculate. */}
          {!analysis.sensitivityResults &&
            (analysis.sensitivityStatus === 'queued' ||
              analysis.sensitivityStatus === 'running') && (
              <section>
                <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-4 text-sm text-indigo-700">
                  Sensitivity analysis{' '}
                  {analysis.sensitivityStatus === 'running' ? 'running' : 'queued'} — results will
                  appear shortly.
                </div>
              </section>
            )}
          {analysis.sensitivityStatus === 'failed' && !analysis.sensitivityResults && (
            <section>
              <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                Sensitivity analysis failed. Re-run the analysis (Edit &amp; Re-run) to try again.
              </div>
            </section>
          )}
          {analysis.sensitivityResults && <SensitivitySection sens={analysis.sensitivityResults} />}

          {/* Multi-District Summary — shown when at least one district impact exists */}
          {(r.countyImpact || r.isdImpact || r.specialDistrictImpact) && (
            <section>
              <h3 className="mb-4 font-display text-2xl leading-tight text-slate-900 inline-flex items-center gap-2">
                Multi-District Summary
                <InfoTooltip label="Multi-District Summary">
                  A project&rsquo;s fiscal impact isn&rsquo;t felt only by the city. The county,
                  school district (ISD), and any special districts (emergency services, hospital,
                  etc.) each collect property taxes and bear their own service costs. This view
                  shows the 10-year net to each taxing body, plus the combined total across all
                  jurisdictions.
                </InfoTooltip>
              </h3>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                {r.countyImpact && (
                  <KpiCard
                    label="County 10-yr net"
                    value={fmtCurrency(r.countyImpact.tenYearNetBenefits)}
                    sub="Property tax − service costs"
                  />
                )}
                {r.isdImpact && (
                  <KpiCard
                    label="ISD 10-yr net"
                    value={fmtCurrency(r.isdImpact.tenYearNetBenefits)}
                    sub="Property tax − student costs"
                  />
                )}
                {r.specialDistrictImpact && (
                  <KpiCard
                    label="Special district 10-yr net"
                    value={fmtCurrency(r.specialDistrictImpact.tenYearNetBenefits)}
                    sub="Property tax − operating costs"
                  />
                )}
                <KpiCard
                  label="All districts combined"
                  value={fmtCurrency(
                    r.summary.tenYearNetBenefits +
                      (r.countyImpact?.tenYearNetBenefits ?? 0) +
                      (r.isdImpact?.tenYearNetBenefits ?? 0) +
                      (r.specialDistrictImpact?.tenYearNetBenefits ?? 0),
                  )}
                  sub="City + county + ISD + special"
                />
              </div>
            </section>
          )}

          {/* District fiscal impact tabs */}
          {availableTabs.length > 1 && (
            <section>
              <h3 className="mb-4 font-display text-2xl leading-tight text-slate-900 inline-flex items-center gap-2">
                District Fiscal Impact
                <InfoTooltip label="District Fiscal Impact">
                  Year-by-year breakdown for each taxing jurisdiction. Use the tabs to compare how
                  benefits accrue and when cumulative net turns positive — important for districts
                  whose service costs (schools especially) can front-load before property tax
                  revenue catches up.
                </InfoTooltip>
              </h3>

              {/* Tab bar */}
              <div className="mb-4 flex w-fit gap-1 rounded-lg border border-slate-200 bg-slate-50 p-1">
                {availableTabs.map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setDistrictTab(tab.key)}
                    className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                      districtTab === tab.key
                        ? 'bg-white text-slate-900 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* City fiscal table (existing breakdown) */}
              {districtTab === 'city' && (
                <div className="overflow-x-auto rounded-lg border border-slate-200">
                  <table className="min-w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-2 text-left font-medium text-slate-500">Year</th>
                        <th className="px-4 py-2 text-right font-medium text-slate-500">
                          Benefits
                        </th>
                        <th className="px-4 py-2 text-right font-medium text-slate-500">Costs</th>
                        <th className="px-4 py-2 text-right font-medium text-slate-500">
                          Net Benefit
                        </th>
                        <th className="px-4 py-2 text-right font-medium text-slate-500">
                          Cumulative
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {r.cityImpact.annualNetBenefits.map((_, i) => (
                        // biome-ignore lint/suspicious/noArrayIndexKey: year index is stable
                        <tr key={i} className="hover:bg-slate-50">
                          <td className="px-4 py-2 text-slate-500">Year {i + 1}</td>
                          <td className="px-4 py-2 text-right">
                            {fmtCurrency(r.cityImpact.benefitsSubtotal[i] ?? 0)}
                          </td>
                          <td className="px-4 py-2 text-right text-red-600">
                            {fmtCurrency(r.cityImpact.costsSubtotal[i] ?? 0)}
                          </td>
                          <td
                            className={`px-4 py-2 text-right font-medium ${(r.cityImpact.annualNetBenefits[i] ?? 0) >= 0 ? 'text-green-700' : 'text-red-600'}`}
                          >
                            {fmtCurrency(r.cityImpact.annualNetBenefits[i] ?? 0)}
                          </td>
                          <td className="px-4 py-2 text-right text-slate-600">
                            {fmtCurrency(r.cityImpact.cumulativeNetBenefits[i] ?? 0)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-slate-50 font-medium">
                      <tr>
                        <td className="px-4 py-2 text-slate-700">Total</td>
                        <td colSpan={2} />
                        <td
                          className={`px-4 py-2 text-right ${r.summary.tenYearNetBenefits >= 0 ? 'text-green-700' : 'text-red-600'}`}
                        >
                          {fmtCurrency(r.summary.tenYearNetBenefits)}
                        </td>
                        <td />
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}

              {districtTab === 'county' && r.countyImpact && (
                <DistrictImpactTable
                  impact={r.countyImpact}
                  entityMetrics={r.summary.perEntity?.county}
                />
              )}
              {districtTab === 'isd' && r.isdImpact && (
                <DistrictImpactTable
                  impact={r.isdImpact}
                  entityMetrics={r.summary.perEntity?.isd}
                />
              )}
              {districtTab === 'special' && r.specialDistrictImpact && (
                <DistrictImpactTable
                  impact={r.specialDistrictImpact}
                  entityMetrics={r.summary.perEntity?.special}
                />
              )}
            </section>
          )}

          {/* 10-Year Trend Chart */}
          <section>
            <h3 className="mb-4 font-display text-2xl leading-tight text-slate-900 inline-flex items-center gap-2">
              10-Year Trend
              <InfoTooltip label="10-Year Trend">
                How the project ramps over time. <strong>Total jobs</strong> (left axis) climbs as
                hiring stabilizes. <strong>Net benefits</strong> (right axis) is the annual fiscal
                impact; <strong>cumulative net</strong> is the running total — where that line
                crosses zero is your payback point.
              </InfoTooltip>
            </h3>
            <div className="rounded-2xl bg-white/60 backdrop-blur-xl border border-white/60 shadow-lg shadow-slate-900/5 p-5">
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={trendData} margin={{ top: 4, right: 24, bottom: 4, left: 16 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="year" tick={{ fontSize: 11 }} />
                  <YAxis
                    yAxisId="jobs"
                    orientation="left"
                    tick={{ fontSize: 11 }}
                    tickFormatter={(v: number) => fmtNum(v, 0)}
                    label={{ value: 'Jobs', angle: -90, position: 'insideLeft', fontSize: 11 }}
                  />
                  <YAxis
                    yAxisId="dollars"
                    orientation="right"
                    tick={{ fontSize: 11 }}
                    tickFormatter={(v: number) => fmtK(v)}
                  />
                  <Tooltip
                    formatter={(value, name) => {
                      const v = Number(value);
                      return String(name) === 'Total Jobs'
                        ? [fmtNum(v, 1), name]
                        : [fmtCurrency(v), name];
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line
                    yAxisId="jobs"
                    type="monotone"
                    dataKey="Total Jobs"
                    stroke="#6366f1"
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line
                    yAxisId="dollars"
                    type="monotone"
                    dataKey="Net Benefits"
                    stroke="#10b981"
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line
                    yAxisId="dollars"
                    type="monotone"
                    dataKey="Cumulative Net"
                    stroke="#0ea5e9"
                    strokeWidth={2}
                    strokeDasharray="5 3"
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </section>

          {/* Year-10 Fiscal Waterfall */}
          {waterfallData.length > 0 && (
            <section>
              <h3 className="mb-4 font-display text-2xl leading-tight text-slate-900 inline-flex items-center gap-2">
                Year-{years} Fiscal Breakdown
                <InfoTooltip label={`Year-${years} Fiscal Breakdown`}>
                  Where the money comes from (and goes) in the final projected year, when the
                  project is fully stabilized. Positive bars are city revenues by category (sales
                  tax, property tax, etc.); the negative bar is the cost of providing city services
                  to the new activity.
                </InfoTooltip>
              </h3>
              <div className="rounded-2xl bg-white/60 backdrop-blur-xl border border-white/60 shadow-lg shadow-slate-900/5 p-5">
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart
                    data={waterfallData}
                    margin={{ top: 4, right: 24, bottom: 4, left: 16 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v: number) => fmtK(v)} />
                    <Tooltip formatter={(v) => [fmtCurrency(Number(v)), 'Amount']} />
                    <Bar dataKey="value" fill="#6366f1" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
                <p className="mt-2 text-xs text-slate-400">
                  Positive = city revenue. Negative = cost of services.
                </p>
              </div>
            </section>
          )}
        </>
      )}

      {/* Validation Report Appendix — only shown when bypasses were recorded */}
      {validationReport && validationReport.bypasses.length > 0 && (
        <section>
          <h3 className="mb-4 font-display text-2xl leading-tight text-slate-900 inline-flex items-center gap-2">
            Validation Report
            <InfoTooltip label="Validation Report">
              Benchmark flags the analyst reviewed and knowingly accepted. When an input falls
              outside typical ranges for the project type, the system warns the analyst — they can
              proceed, but must justify why. This appendix documents those justifications for the
              record.
            </InfoTooltip>
          </h3>
          <div className="rounded-2xl bg-white/60 backdrop-blur-xl border border-white/60 shadow-lg shadow-slate-900/5 p-6 space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-600">
                {validationReport.bypasses.length} benchmark flag
                {validationReport.bypasses.length !== 1 ? 's' : ''} acknowledged by analyst.
              </p>
              <span className="text-xs text-slate-400">
                Submitted{' '}
                {new Date(validationReport.validatedAt).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                })}
              </span>
            </div>
            <div className="divide-y divide-slate-100">
              {(
                validationReport.bypasses as Array<{
                  field: string;
                  metric: string;
                  severity: string;
                  analystValue: number;
                  derivedValue: number;
                  derivedLabel: string;
                  justification: string;
                }>
              ).map((b) => (
                <div key={b.field} className="py-4 first:pt-0 last:pb-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span
                      className={`rounded px-2 py-0.5 text-xs font-semibold uppercase tracking-wide ${
                        b.severity === 'warning'
                          ? 'bg-red-100 text-red-700'
                          : 'bg-amber-100 text-amber-700'
                      }`}
                    >
                      {b.severity}
                    </span>
                    <span className="text-sm font-medium text-slate-700">{b.metric}</span>
                  </div>
                  <p className="text-xs text-slate-500 mb-2">
                    Analyst value: {fmtCurrency(b.analystValue)} → {fmtCurrency(b.derivedValue)}{' '}
                    {b.derivedLabel}
                  </p>
                  <div className="rounded-md bg-slate-50 px-3 py-2">
                    <p className="text-xs font-medium text-slate-500 mb-0.5">Justification</p>
                    <p className="text-sm text-slate-700">{b.justification}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Comment thread — available to all roles including reviewer */}
      {id && <AnalysisComments analysisId={id} />}
    </div>
  );
}
